import { Component, OnInit, ViewChild } from '@angular/core';
import { InvoicesService, LathamOrdersService, SleepService, HttpService, UtilsService, LineItemsService, GlobalsService } from '@services';
import { IDropdownModel, IInventoryLocationsListModel, IInvoiceModel, ILathamOrderModel, ILineItemModel, IPurchaseOrderModel, LineItemTypes } from '@models';

import { SlickDialogComponent, SlickToastService } from "@slick-components";
import *  as moment from 'moment';
import { InventoryStore } from '@stores';

export interface ICompareLathamBillDialogOptions {
	shippingCharge?: number;
	receivingWarehouseId?: number;
	shipDate?: Date | string;
	lathamInvoiceNumber?: string;
	openInvoiceEditor?: (invoiceId: number) => Promise<boolean>;
	batchMode?: boolean;
}

export interface ICompareLathamBillDialogResult {
	invoiceId: number;
	poCreated: boolean;
	poSyncedToBc: boolean;
	bcSyncError?: string;
	invoiceFixed: boolean;
	invoiceEdited: boolean;
}

@Component({
	selector: 'compare-latham-bill-dialog',
	templateUrl: './compare-latham-bill-dialog.component.html',
	styleUrls: ['./compare-latham-bill-dialog.component.css'],
	providers: [InvoicesService, LineItemsService, LathamOrdersService, SlickToastService]
})
export class CompareLathamBillDialogComponent {
	private static readonly FREIGHT_PREFILL_KEY = 'COMPARE_LATHAM_BILL_FREIGHT_PREFILL';

	@ViewChild("dialogRef") dialogRef: SlickDialogComponent;
	res: (result: ICompareLathamBillDialogResult) => void;
	loading: boolean = false;
	invoiceWasFixed: boolean = false;
	invoiceWasEdited: boolean = false;
	batchMode: boolean = false;
	lastBcSyncError: string;
	openInvoiceEditorFn: (invoiceId: number) => Promise<boolean>;
	private pendingShipDate: Date | string;
	private pendingLathamInvoiceNumber: string;
	invoiceModel: IInvoiceModel;
	lathamOrder: ILathamOrderModel;
	lathamSalesOrder: ILathamOrderModel;
	purchaseOrder: IPurchaseOrderModel;
	spinnerStatus: string;
	poSpinnerStatus: string;
	shipDate: Date = (moment(localStorage.getItem("COMPARE_LATHAM_BILL_SHIP_DATE"), "MM/DD/YYYY").startOf("date").toDate() || moment().startOf("date").toDate())
	inventoryWarehouseId: number = parseInt(localStorage.getItem("COMPARE_LATHAM_BILL_WAREHOUSE_ID"));
	inventoryWarehouses: IDropdownModel[]
	locationId: number;
	shippingCharge: number;
	lathamInvoiceNumber: string;
	lathamCustomerPO: string;
	skeditLineItems: SkeditLineItem[] = [];
	lathamLineItems: LathamLineItem[] = [];

	displayLineItems: DisplayLineItem[] = [];

	invalidDate: boolean = false;
	submitted: boolean = false;
	constructor(private readonly httpService: HttpService,
		private readonly invoicesService: InvoicesService,
		private readonly inventoryStore: InventoryStore,
		private readonly lineItemsService: LineItemsService,
		private readonly lathamOrdersService: LathamOrdersService) {
		this.inventoryWarehouses = this.inventoryStore.getInventoryWarehousesForDropdown();
	}

	static setFreightPrefill(orderId: string | number, freight: number): void {
		const key = CompareLathamBillDialogComponent.normalizeFreightPrefillKey(orderId);
		if (!key || freight == null)
			return;

		const map = CompareLathamBillDialogComponent.getFreightPrefillMap();
		map[key] = freight;
		localStorage.setItem(CompareLathamBillDialogComponent.FREIGHT_PREFILL_KEY, JSON.stringify(map));
	}

	static setFreightPrefillFromParseRows(rows: { orderId?: string; freight?: number }[]): void {
		if (!rows?.length)
			return;

		const map = CompareLathamBillDialogComponent.getFreightPrefillMap();
		for (const row of rows) {
			const key = CompareLathamBillDialogComponent.normalizeFreightPrefillKey(row.orderId);
			if (key && row.freight != null)
				map[key] = row.freight;
		}
		localStorage.setItem(CompareLathamBillDialogComponent.FREIGHT_PREFILL_KEY, JSON.stringify(map));
	}

	static consumeFreightPrefill(orderId: string | number): number | null {
		const key = CompareLathamBillDialogComponent.normalizeFreightPrefillKey(orderId);
		if (!key)
			return null;

		const map = CompareLathamBillDialogComponent.getFreightPrefillMap();
		if (!(key in map))
			return null;

		const freight = map[key];
		delete map[key];
		localStorage.setItem(CompareLathamBillDialogComponent.FREIGHT_PREFILL_KEY, JSON.stringify(map));
		return freight ?? null;
	}

	private static normalizeFreightPrefillKey(orderId: string | number): string {
		if (orderId == null)
			return '';

		return String(orderId).trim();
	}

	private static getFreightPrefillMap(): Record<string, number> {
		try {
			return JSON.parse(localStorage.getItem(CompareLathamBillDialogComponent.FREIGHT_PREFILL_KEY) || '{}');
		}
		catch {
			return {};
		}
	}

	async openDialog(invoiceModel: IInvoiceModel, options?: ICompareLathamBillDialogOptions): Promise<ICompareLathamBillDialogResult> {
		this.spinnerStatus = "reset";
		this.poSpinnerStatus = "reset";
		this.invoiceWasFixed = false;
		this.invoiceWasEdited = false;
		this.lastBcSyncError = null;
		this.batchMode = options?.batchMode ?? false;
		this.openInvoiceEditorFn = options?.openInvoiceEditor ?? null;
		this.shippingCharge = options?.shippingCharge ?? 0;
		this.pendingShipDate = options?.shipDate ?? null;
		this.pendingLathamInvoiceNumber = options?.lathamInvoiceNumber ?? null;
		if (options?.receivingWarehouseId) {
			this.inventoryWarehouseId = options.receivingWarehouseId;
			localStorage.setItem("COMPARE_LATHAM_BILL_WAREHOUSE_ID", options.receivingWarehouseId.toString());
		}
		this.lathamInvoiceNumber = null;
		this.lathamCustomerPO = null;

		return new Promise(async (res) => {
			this.res = res;
			this.loading = true;
			this.invoiceModel = invoiceModel;

			this.lathamSalesOrder = null;
			this.skeditLineItems = [];
			this.lathamLineItems = [];

			this.dialogRef.showDialog();

			//const json = localStorage.getItem("LATHAM_BILL_TEST");
			//if (json) {
			//	this.copyFromJSON(json);
			//	this.loading = false;
			//	return;
			//}

			await this.refresh();

			localStorage.setItem("LATHAM_BILL_TEST", JSON.stringify(this.displayLineItems));
			this.loading = false;
		})
	}

	async process() {
		this.spinnerStatus = "spin";

		try {
			const displayLineItemsJSON = JSON.stringify(this.displayLineItems);
			const params = {
				invoiceId: this.invoiceModel.invoiceId,
				displayLineItemsJSON: displayLineItemsJSON
			}
			await this.httpService.post(`/lathamBills/saveLathamBillHistory`, params);

			// Make a copy of the line items currently in use so we know which ones were added
			const originalLineItems: ILineItemModel[] = UtilsService.clone(this.invoiceModel.lineItems);

			const dto = new LathamBillCompareDTO();
			dto.skeditLineItems = this.skeditLineItems;
			dto.lathamLineItems = this.lathamLineItems;

			await this.httpService.post(`/lathamBills/fixLathamBill?invoiceId=${this.invoiceModel.invoiceId}`, dto);

			this.invoiceModel = await this.invoicesService.getInvoice(this.invoiceModel.invoiceId);			

			// Since Invoices are supposed to be locked as far as pricing goes, only update new line items,
			// Only update the pricing if we've added a new item with a $0 price amount
			if (this.invoiceModel.lineItems.findIndex(li => li.lineItemType === LineItemTypes.LineItem && li.price === 0) >= 0) {
				// The refreshPricing will refresh the array passed in as well as return it, so make a copy of the current line items
				const lineItemsCopy: ILineItemModel[] = UtilsService.clone(this.invoiceModel.lineItems);
				const updatedPricingLineItems = await this.lineItemsService.refreshPricing(lineItemsCopy, this.invoiceModel.customer);

				this.invoiceModel.lineItems.forEach(li => {
					// Check our original line items to see if this one was added by the fix tool
					if (li.lineItemType === LineItemTypes.LineItem && originalLineItems.findIndex(x => x.uuid === li.uuid) < 0) {
						const updatedPricingLineItem = updatedPricingLineItems.find(x => x.itemId === li.itemId);
						if (updatedPricingLineItem)
							li.price = updatedPricingLineItem.price;
					}
				});

				this.invoiceModel.lineItems = this.lineItemsService.recalculateByInvoice(this.invoiceModel);

				if (GlobalsService.company.useInventory && this.invoiceModel.useDefaultInventory)
					this.invoiceModel.inventoryLineItems = this.invoicesService.syncInventoryFromLineItems(this.invoiceModel);

				this.invoiceModel = await this.invoicesService.updateInvoice(this.invoiceModel);
				this.lathamOrder.invoice = this.invoiceModel;
			}

			await this.refresh();

			await SleepService.sleep(500);
			this.invoiceWasFixed = true;
			this.spinnerStatus = "Ok";
			await this.closeDialog();
		}
		catch {
			this.spinnerStatus = "error";
		}
	}

	async generatePO() {
		this.poSpinnerStatus = "spin";
		this.submitted = true;
		this.invalidDate = false;
		let dateCheck = new Date(this.shipDate);
		if (isNaN(dateCheck.getTime())) {
			this.invalidDate = true;
			this.poSpinnerStatus = "error";
			return;
		}

		if (!this.inventoryWarehouseId) {
			return;
		}

		try {
			await SleepService.sleep(500);

			this.lathamOrder.invoice = this.invoiceModel;
			this.lathamOrder.lathamInvoiceNumber = this.lathamInvoiceNumber;
			this.lathamOrder.lathamCustomerPO = this.lathamCustomerPO;

			const dateIsoString = moment(this.shipDate).toISOString();

			this.purchaseOrder = await this.httpService.post(`purchaseOrders/addLPPPurchaseOrder?shipDate=${dateIsoString}&receivingWarehouseId=${this.inventoryWarehouseId}&shippingCharge=${this.shippingCharge}`, this.lathamOrder);

			this.lastBcSyncError = this.isPoSyncedToBc ? null : 'BC sync pending';
			this.poSpinnerStatus = this.isPoSyncedToBc ? "ok" : "error";
			await this.closeDialog();
		}
		catch (err: any) {
			console.error(err);
			this.lastBcSyncError = err?.error?.message || err?.message || 'BC sync failed';
			this.poSpinnerStatus = "error";
			await this.refresh();
		}
	}

	async retryBcSync() {
		if (!this.batchMode || !this.invoiceModel?.invoiceId || !this.purchaseOrder || this.isPoSyncedToBc)
			return;

		this.poSpinnerStatus = "spin";
		this.lastBcSyncError = null;

		try {
			this.purchaseOrder = await this.httpService.post(`/lathamBills/syncPurchaseOrderToBusinessCentral?invoiceId=${this.invoiceModel.invoiceId}`, null);
			this.poSpinnerStatus = "ok";
		}
		catch (err: any) {
			console.error(err);
			this.lastBcSyncError = err?.error?.message || err?.message || 'BC sync failed';
			this.poSpinnerStatus = "error";
			await this.refresh();
		}
	}

	get isPoSyncedToBc(): boolean {
		return !!(this.purchaseOrder?.businessCentralId);
	}

	get showBcSyncPending(): boolean {
		return !!this.purchaseOrder && !this.isPoSyncedToBc;
	}

	onInventoryWarehouseSelect(inventoryWarehouse: IDropdownModel) {
		this.inventoryWarehouseId = inventoryWarehouse.id;
		localStorage.setItem("COMPARE_LATHAM_BILL_WAREHOUSE_ID", inventoryWarehouse.id.toString());
	}

	onShipDateChanged(date: Date) {
		localStorage.setItem("COMPARE_LATHAM_BILL_SHIP_DATE", moment(date).format('MM/DD/YYYY'));
	}

	private async closeDialog() {
		if (!this.dialogRef)
			return;

		await this.dialogRef.hideDialog();
	}

	onDialogClose() {
		this.res({
			invoiceId: this.invoiceModel?.invoiceId,
			poCreated: !!this.purchaseOrder,
			poSyncedToBc: this.isPoSyncedToBc,
			bcSyncError: this.lastBcSyncError,
			invoiceFixed: this.invoiceWasFixed,
			invoiceEdited: this.invoiceWasEdited
		});
	}

	async openInvoiceEditor() {
		if (!this.invoiceModel?.invoiceId || !this.openInvoiceEditorFn)
			return;

		const edited = await this.openInvoiceEditorFn(this.invoiceModel.invoiceId);
		if (!edited)
			return;

		this.invoiceModel = await this.invoicesService.getInvoice(this.invoiceModel.invoiceId);
		this.invoiceWasEdited = true;
		await this.refresh();
	}

	// Latham dates come back as UTC midnight (e.g. 2026-06-23T00:00:00Z). Parsing in local time
	private toLocalDateOnly(date: string | Date): Date {
		const utc = moment.utc(date);
		return new Date(utc.year(), utc.month(), utc.date());
	}

	async refresh(): Promise<void> {

		// Need to get the latham order so we can get the latham order id
		this.purchaseOrder = await this.httpService.get(`/purchaseOrders/getPurchaseOrderByInvoiceId?invoiceId=${this.invoiceModel.invoiceId}`);

		if (!this.lathamSalesOrder) {
			this.lathamOrder = await this.lathamOrdersService.getLathamOrder(this.invoiceModel.lathamOrderId);
			this.lathamSalesOrder = await this.lathamOrdersService.getLathamOrderFromSalesOrder(this.lathamOrder.orderId);
		}

		this.lathamInvoiceNumber = this.lathamSalesOrder.lathamInvoiceNumber;
		if (!this.lathamInvoiceNumber?.trim() && this.pendingLathamInvoiceNumber)
			this.lathamInvoiceNumber = this.pendingLathamInvoiceNumber;

		this.lathamCustomerPO = this.lathamSalesOrder.lathamCustomerPO;
		if (this.lathamCustomerPO && !this.lathamCustomerPO.includes('/')) {
			const orderIdSuffix = this.lathamOrder?.orderId || this.invoiceModel?.invoiceNumber;
			if (orderIdSuffix)
				this.lathamCustomerPO = `${this.lathamCustomerPO}/${orderIdSuffix}`;
		}

		if (this.lathamSalesOrder.lathamShipDate)
			this.shipDate = this.toLocalDateOnly(this.lathamSalesOrder.lathamShipDate);
		else if (this.pendingShipDate)
			this.shipDate = this.toLocalDateOnly(this.pendingShipDate);

		this.skeditLineItems = this.invoiceModel.lineItems
			.filter(x => (x.isGlobalItem && x.lineItemType === LineItemTypes.LineItem && x.sendToManufacturer === true)
				// LEGACY GUARD: keep Discount-type A2921/A2922 visible on the Skedit side so an old discount
				// order still matches Latham (and a compare+fix doesn't add a duplicate). Inert for new orders.
				|| (x.lineItemType === LineItemTypes.Discount && ["A2921", "A2922"].includes((x.sku ?? "").trim().toUpperCase())))
			.map(li => {
				const newSkeditLineItem = new SkeditLineItem();
				newSkeditLineItem.lineItemUuid = li.uuid;
				newSkeditLineItem.sku = (li.sku ?? "").trim().toUpperCase();
				newSkeditLineItem.description = li.description;
				newSkeditLineItem.displayOrder = li.displayOrder;
				newSkeditLineItem.cost = li.cost;
				newSkeditLineItem.quantity = li.quantity;

				// LEGACY GUARD: A2921/A2922 entered as Discount lines store quantity = fabric sq ft; normalize to
				// 1 so they match Latham's line. Inert for new orders (those are a LineItem at qty 1). Remove with
				// the other legacy guards once no Discount-type A2921/A2922 remain.
				if (li.lineItemType === LineItemTypes.Discount && ["A2921", "A2922"].includes(newSkeditLineItem.sku))
					newSkeditLineItem.quantity = 1;

				return newSkeditLineItem;
			});

		this.lathamLineItems = this.lathamSalesOrder.lathamSalesOrderLineItems
			.map(li => {
				const newLathamLineItem = new LathamLineItem();
				newLathamLineItem.sku = (li.sku ?? "").trim().toUpperCase();
				newLathamLineItem.description = li.description;
				newLathamLineItem.price = li.price;
				newLathamLineItem.quantity = li.quantity;
				newLathamLineItem.priceMatchFound = true;
				newLathamLineItem.skuMatchFound = true;

				return newLathamLineItem;
			});

		this.skeditLineItems.forEach(li => {
			const lathamLineItem = this.lathamLineItems.find(x => x.sku === li.sku);
			if (!lathamLineItem) {
				li.skuMatchFound = false;
				li.priceMatchFound = false;
				li.quantityMatchFound = false;
			}
			else {
				if (li.cost !== lathamLineItem.price)
					li.priceMatchFound = false;

				if (li.quantity !== lathamLineItem.quantity)
					li.quantityMatchFound = false;
			}
		})

		this.lathamLineItems.forEach(li => {
			const skeditLineItem = this.skeditLineItems.find(x => x.sku === li.sku);
			if (!skeditLineItem)
				li.skuMatchFound = false;
			else {
				if (li.price !== skeditLineItem.cost)
					li.priceMatchFound = false;

				if (li.quantity !== skeditLineItem.quantity)
					li.quantityMatchFound = false;
			}
		})

		this.recalc();
	}

	recalc() {
		this.displayLineItems = [];

		this.skeditLineItems
			.filter(skli => skli.skuMatchFound === true)
			.forEach(skli => {
				const displayLineItem = new DisplayLineItem();

				displayLineItem.skeditLineItem = skli;
				displayLineItem.lathamLineItem = this.lathamLineItems.find(x => x.sku == skli.sku);
				this.displayLineItems.push(displayLineItem);
			})

		this.skeditLineItems
			.filter(skli => skli.skuMatchFound === false)
			.forEach(skli => {
				const displayLineItem = new DisplayLineItem();

				displayLineItem.skeditLineItem = skli;
				displayLineItem.lathamLineItem = null;
				this.displayLineItems.push(displayLineItem);
			})

		this.lathamLineItems
			.filter(lali => lali.skuMatchFound === false)
			.forEach(lali => {
				lali.quantityMatchFound = false;
				lali.priceMatchFound = false;

				const emptyLathamLineItem = this.displayLineItems.find(x => x.lathamLineItem === null);
				if (emptyLathamLineItem) {
					emptyLathamLineItem.lathamLineItem = lali;
				}
				else {
					const displayLineItem = new DisplayLineItem();

					displayLineItem.skeditLineItem = null;
					displayLineItem.lathamLineItem = lali;
					this.displayLineItems.push(displayLineItem);
				}
			})
	}

	private copyFromJSON(displayLineItemsJSON: string) {
		const displayLineItems: DisplayLineItem[] = JSON.parse(displayLineItemsJSON);

		this.displayLineItems = displayLineItems.map(li => {
			let returnLineItem = new DisplayLineItem();
			returnLineItem.skeditLineItem = UtilsService.clone(li.skeditLineItem);
			returnLineItem.lathamLineItem = UtilsService.clone(li.lathamLineItem);

			return returnLineItem;
		})
		this.skeditLineItems = this.displayLineItems
			.filter(x => x.skeditLineItem != null)
			.map(x => x.skeditLineItem);
		this.lathamLineItems = this.displayLineItems
			.filter(x => x.lathamLineItem != null)
			.map(x => x.lathamLineItem);
	}
}

class SkeditLineItem {
	constructor() {
		this.skuMatchFound = true;
		this.priceMatchFound = true;
		this.quantityMatchFound = true;
	}

	lineItemUuid: string;
	sku: string;
	description: string;
	displayOrder: number;
	cost: number;
	quantity: number;
	skuMatchFound: boolean;
	priceMatchFound: boolean;
	quantityMatchFound: boolean;
}

class LathamLineItem {
	constructor() {
		this.skuMatchFound = true;
		this.priceMatchFound = true;
		this.quantityMatchFound = true;
	}

	sku: string;
	description: string;
	price: number;
	quantity: number;
	skuMatchFound: boolean;
	priceMatchFound: boolean;
	quantityMatchFound: boolean;
}

class DisplayLineItem {
	skeditLineItem: SkeditLineItem;
	lathamLineItem: LathamLineItem;
	get classSkeditSku(): string {
		if (!this.skeditLineItem)
			return '';

		if ((this.skeditLineItem?.skuMatchFound ?? false) === true)
			return "bg-success text-white";
		else
			return "bg-danger text-white";
	}

	get classSkeditQty(): string {
		if (!this.skeditLineItem)
			return '';

		if ((this.skeditLineItem?.quantityMatchFound ?? false) === true)
			return "bg-success text-white";
		else
			return "bg-danger text-white";
	}

	get classSkeditCost(): string {
		if (!this.skeditLineItem)
			return '';

		if ((this.skeditLineItem?.priceMatchFound ?? false) === true)
			return "bg-success text-white";
		else
			return "bg-danger text-white";
	}

	get classLathamSku(): string {
		if (!this.lathamLineItem)
			return '';

		if ((this.lathamLineItem?.skuMatchFound ?? false) === true)
			return "bg-success text-white";
		else
			return "bg-danger text-white";
	}

	get classLathamQty(): string {
		if (!this.lathamLineItem)
			return '';

		if ((this.lathamLineItem?.quantityMatchFound ?? false) === true)
			return "bg-success text-white";
		else
			return "bg-danger text-white";
	}

	get classLathamPrice(): string {
		if (!this.lathamLineItem)
			return '';

		if ((this.lathamLineItem?.priceMatchFound ?? false) === true)
			return "bg-success text-white";
		else
			return "bg-danger text-white";
	}
}

class LathamBillCompareDTO {
	skeditLineItems: SkeditLineItem[];
	lathamLineItems: LathamLineItem[];
}